import React from 'react'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Chip,
} from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store/index'
import { setSidebarOpen } from '../../store/slices/appSlice'
import { UserRole } from '../../types/index'
import { getNavigationForRole } from '../../config/navigation'
import { useAuth } from '../../hooks/useAuth'

interface RoleSidebarProps {
  userRole: UserRole | null
}

const drawerWidth = 240

export const RoleSidebar: React.FC<RoleSidebarProps> = ({ userRole }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAuth()
  const { sidebarOpen } = useAppSelector(state => state.app)

  const { mainItems, bottomItems } = getNavigationForRole(userRole)

  const [openMenus, setOpenMenus] = React.useState<{ [key: string]: boolean }>(
    {}
  )

  const handleDrawerToggle = () => {
    dispatch(setSidebarOpen(!sidebarOpen))
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    if (isMobile) dispatch(setSidebarOpen(false))
  }

  const toggleMenu = (text: string) => {
    setOpenMenus(prev => ({ ...prev, [text]: !prev[text] }))
  }

  const isItemActive = (path?: string): boolean => {
    if (!path) return false
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    )
  }

  const getRoleDisplayName = (role: UserRole | null): string => {
    switch (role) {
      case UserRole.CLIENT:
        return 'Patient Portal'
      case UserRole.THERAPIST:
        return 'Provider Portal'
      case UserRole.ADMIN:
        return 'Admin Portal'
      case UserRole.CERTIFICATE_MANAGER:
        return 'Certificate Manager Portal'
      default:
        return 'Portal'
    }
  }

  const getRoleColor = (
    role: UserRole | null
  ): 'primary' | 'secondary' | 'success' | 'warning' => {
    switch (role) {
      case UserRole.CLIENT:
        return 'primary'
      case UserRole.THERAPIST:
        return 'success'
      case UserRole.ADMIN:
        return 'warning'
      default:
        return 'secondary'
    }
  }

  const renderNavItem = (item: (typeof mainItems)[0], level = 0) => {
    const isActive = item.path ? isItemActive(item.path) : false
    const hasChildren = item.children && item.children.length > 0
    const isOpen = openMenus[item.text] || false

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            selected={isActive}
            onClick={() => {
              if (hasChildren) toggleMenu(item.text)
              else if (item.path) handleNavigation(item.path)
            }}
            sx={{
              borderRadius: 2,
              mx: 0.5,
              pl: 2 + level * 2,
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main + '15',
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '25',
                },
              },
              '&:hover': { backgroundColor: theme.palette.action.hover },
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive ? theme.palette.primary.main : 'inherit',
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? theme.palette.primary.main : 'inherit',
                  fontSize: '0.875rem',
                },
              }}
            />
            {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </ListItem>

        {hasChildren && isOpen && (
          <List sx={{ pl: 2 }}>
            {item.children!.map(child => renderNavItem(child, level + 1))}
          </List>
        )}
      </React.Fragment>
    )
  }

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ height: '80px' }} />
      {/* <Box sx={{ px: 2, py: 1 }}>
        <Chip
          label={getRoleDisplayName(userRole)}
          color={getRoleColor(userRole)}
          size="small"
          sx={{ width: '100%', fontWeight: 600, fontSize: '0.75rem' }}
        />
        {user && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.5,
              textAlign: 'center',
              fontSize: '0.7rem',
            }}
          >
            {user.firstName} {user.lastName}
          </Typography>
        )}
      </Box>
      <Divider sx={{ my: 1 }} /> */}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ px: 1 }}>{mainItems.map(item => renderNavItem(item))}</List>
      </Box>

      {bottomItems.length > 0 && (
        <Box>
          <Divider />
          <List sx={{ px: 1, py: 1 }}>
            {bottomItems.map(item => renderNavItem(item))}
          </List>
        </Box>
      )}
    </Box>
  )

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={sidebarOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  )
}

export default RoleSidebar
